import React, {useEffect, useMemo} from 'react';
import {
  Box,
  Flex,
  Table,
  Th,
  Tr,
  Thead,
  TableContainer,
  Td,
  Tbody,
  Text,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tag,
  Tooltip,
  TagLabel,
  useToast,
  Button,
} from '@chakra-ui/react';
import {pluralize} from '../../lib/helpers';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import {format, isAfter} from 'date-fns';
import {useCustomer} from '../../hooks/api/metadata';
import {Agent, AgentTypeLabels} from '../../shared/agent';
import {AgentBuilderModal} from './AgentBuilder/AgentBuilderModal';
import {
  AutoEnrichSchedule,
  getNextAutoEnrichDate,
} from '../../shared/fields/enrichment';
import {
  EventSchedule,
  NotificationNew,
  OverflowMenuVertical,
} from '@carbon/icons-react';
import {PageHeader} from '../PageHeader';
import {
  getAgentCreditCost,
  getAgentCreditEstimateCost,
} from '../../shared/credits';
import {ObjectId} from 'bson';
import {StatusBadge} from './StatusBadge';
import {AgentQuickStartMenu} from './AgentBuilder/AgentQuickStartMenu';
import {CreditCostTag} from '../App/CreditCostTag';
import {WebResearcherIcon} from '../Icons/WebResearcher';
import {useIsKeyplayAdmin} from '../../hooks/api/user';
import {DeleteAgentModal} from './DeleteAgentModal';
import {useCloneAgent} from '../../hooks/api/agents';
import {useAgentLimits} from './useAgentLimits';

export const Agents = () => {
  const agents = useCustomer().agents ?? [];
  const agentBuilderModal = useDisclosure();
  const {limitExceeded, limitMessage} = useAgentLimits();
  const isAdmin = useIsKeyplayAdmin();

  return (
    <Flex direction="column">
      <PageHeader title="AI Agents">
        {isAdmin && (
          <Tooltip label={limitMessage} isDisabled={!limitExceeded}>
            <Button
              colorScheme="kbuttonblue"
              fontWeight="normal"
              onClick={agentBuilderModal.onOpen}
              isDisabled={limitExceeded}
            >
              Create Agent
            </Button>
          </Tooltip>
        )}

        <AgentBuilderModal
          isOpen={agentBuilderModal.isOpen}
          onClose={agentBuilderModal.onClose}
        />
      </PageHeader>
      <AgentQuickStartMenu />

      <Flex direction="column" gap="4" p="4">
        <Box textColor="kgray.300" fontSize="sm">
          {pluralize(agents.length, 'agent', 'agents')}
        </Box>

        <AgentsTable agents={agents} />
      </Flex>
    </Flex>
  );
};

const columnHelper = createColumnHelper<Agent>();
const columns = [
  columnHelper.accessor((agent) => agent, {
    header: 'Agent Name',
    cell: (cell) => cell.getValue().label,
  }),
  columnHelper.accessor('type', {
    header: 'Agent Type',
    cell: (cell) => (
      <Text textColor="kgray.300">{AgentTypeLabels[cell.getValue()]}</Text>
    ),
  }),
  columnHelper.accessor('refreshSettings', {
    id: 'frequency',
    header: 'Frequency',
    cell: (cell) => <RefreshSettings refreshSettings={cell.getValue()} />,
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (cell) => <StatusBadge status={cell.getValue()} />,
  }),
  columnHelper.accessor((agent) => agent, {
    id: 'cost',
    header: 'Cost',
    cell: (cell) => (
      <CreditCost
        agentId={cell.getValue().id}
        agentType={cell.getValue().type}
      />
    ),
  }),
  columnHelper.accessor('timestamp', {
    header: 'Created',
    cell: (cell) => format(cell.getValue(), 'M/dd/yy'),
  }),
  columnHelper.accessor((agent) => agent, {
    id: 'actions',
    header: '',
    size: 50,
    cell: (cell) => <AgentMenu agent={cell.getValue()} />,
  }),
];

const AgentMenu = ({agent}: {agent: Agent}) => {
  const agentBuilderModal = useDisclosure();
  const deleteAgentModal = useDisclosure();
  const isKeyplayAdmin = useIsKeyplayAdmin();
  const cloneAgent = useCloneAgent();
  const toast = useToast();

  useEffect(() => {
    if (cloneAgent.isPending) {
      toast({
        id: 'clone-agent',
        title: 'Cloning agent...',
        status: 'info',
      });
    } else {
      toast.close('clone-agent');
    }

    return () => toast.close('clone-agent');
  }, [cloneAgent.isPending, toast]);

  return (
    <Menu>
      <MenuButton>
        <OverflowMenuVertical />
      </MenuButton>
      <MenuList>
        <MenuItem onClick={agentBuilderModal.onOpen}>
          {agent.status === 'draft' ? 'Edit Agent' : 'Review Agent'}
          <AgentBuilderModal
            isOpen={agentBuilderModal.isOpen}
            onClose={agentBuilderModal.onClose}
            agentId={agent.id}
            initialStep={
              agent.status === 'draft' ? 'setName' : 'reviewSettings'
            }
          />
        </MenuItem>
        <MenuItem
          isDisabled={cloneAgent.isPending}
          onClick={() => cloneAgent.mutate({agentId: agent.id})}
        >
          Clone Agent
        </MenuItem>
        {(isKeyplayAdmin || agent.status === 'draft') && (
          <MenuItem onClick={deleteAgentModal.onOpen} textColor="kred.500">
            Delete Agent
            <DeleteAgentModal
              agent={agent}
              isOpen={deleteAgentModal.isOpen}
              onClose={deleteAgentModal.onClose}
            />
          </MenuItem>
        )}
      </MenuList>
    </Menu>
  );
};

const AgentsTable = ({agents}: {agents: Agent[]}) => {
  const sortedAgents = useMemo(
    () => [...agents].sort((a, b) => a.label.localeCompare(b.label)),
    [agents]
  );

  const table = useReactTable({
    columns,
    data: sortedAgents,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
    defaultColumn: {
      minSize: 0,
      size: 150,
    },
  });

  if (!sortedAgents.length) {
    return (
      <Flex direction="column" align="center" p="8" gap="4">
        <WebResearcherIcon h="160px" w="160px" />
        <Text>No agents added yet</Text>
        <Text textColor="kgray.300" textAlign="center">
          Use AI Agents to summarize data, create structured outputs and more.
        </Text>
      </Flex>
    );
  }

  return (
    <TableContainer border="1px" borderColor="kgray.200" borderRadius="md">
      <Table variant="simple">
        <Thead bg="kgray.100">
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th
                  key={header.id}
                  w={header.getSize() ? `${header.getSize()}px` : 'auto'}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id} role="group">
              {row.getVisibleCells().map((cell) => (
                <Td
                  key={cell.id}
                  _groupHover={{bg: 'kgray.100'}}
                  w={
                    cell.getContext().column.getSize()
                      ? `${cell.getContext().column.getSize()}px`
                      : 'auto'
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

const AgentRefreshScheduleLabels: Record<
  Exclude<AutoEnrichSchedule['schedule'], undefined>,
  {tooltip: string; tag: string}
> = {
  oneMonth: {tooltip: 'Auto-refresh every month', tag: '1M'},
  threeMonths: {tooltip: 'Auto-refresh every 3 months', tag: '3M'},
  sixMonths: {tooltip: 'Auto-refresh every 6 months', tag: '6M'},
  oneYear: {tooltip: 'Auto-refresh every year', tag: '1Y'},
};
const RefreshSettings = ({
  refreshSettings,
}: {
  refreshSettings: Agent['refreshSettings'];
}) => {
  const autoEnrichNewAccounts = refreshSettings?.autoEnrichNewAccounts;
  const autoRefreshSchedule = refreshSettings?.autoEnrichSchedule?.schedule;
  const cutoffDate = refreshSettings?.autoEnrichSchedule?.cutoffDate;

  const nextRunDate = getNextAutoEnrichDate({
    cutoffDate,
    schedule: autoRefreshSchedule,
  });

  const nextRunText =
    nextRunDate &&
    `Next run: ${
      isAfter(nextRunDate, new Date())
        ? format(nextRunDate, 'MM/dd/yy')
        : 'tonight'
    }`;

  const getTooltipLabel = (
    schedule: NonNullable<AutoEnrichSchedule['schedule']>
  ) => {
    const autoRefreshText = AgentRefreshScheduleLabels[schedule].tooltip;

    if (!nextRunText) {
      return autoRefreshText;
    }

    return `${autoRefreshText}. ${nextRunText}`;
  };

  return (
    <Flex align="center" gap="2">
      {/* Auto-refresh interval indicator */}
      {autoRefreshSchedule && (
        <Tooltip label={getTooltipLabel(autoRefreshSchedule)}>
          <Tag gap="1">
            <EventSchedule />
            <TagLabel fontSize="xs">
              {AgentRefreshScheduleLabels[autoRefreshSchedule].tag}
            </TagLabel>
          </Tag>
        </Tooltip>
      )}

      {/* Auto Run on New Accounts indicator - only show if true */}
      {autoEnrichNewAccounts && (
        <Tooltip label="Automatically runs on new accounts">
          <Tag>
            <NotificationNew />
          </Tag>
        </Tooltip>
      )}
    </Flex>
  );
};

const CreditCost = ({
  agentId,
  agentType,
}: {
  agentId: ObjectId;
  agentType: Agent['type'];
}) => {
  const {fieldDefinitions} = useCustomer();
  const numCredits = getAgentCreditCost(agentId, fieldDefinitions);
  const estimatedCost = getAgentCreditEstimateCost(agentType);
  return <CreditCostTag creditCost={numCredits ?? estimatedCost} />;
};
